import { siteInfo, faqs, testimonials } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://www.911heilian.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <main className="pt-20 pb-12">
        <section className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <div className="text-center mb-12">
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mb-4">
              关于911黑料网
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              我们是一群追求真相的媒体人，致力于为公众提供最真实、最及时的独家内幕资讯。
            </p>
          </div>

          <div className="relative w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20newsroom%20with%20multiple%20monitors%20displaying%20breaking%20news%2C%20journalists%20working%20at%20desks%2C%20warm%20lighting%2C%20professional%20atmosphere%2C%20investigative%20journalism%20office%2C%20editorial%20environment%2C%20clean%20modern%20workspace&width=1200&height=500&seq=about-hero&orientation=landscape"
              alt="911黑料网编辑部"
              title="911黑料网 - 专业新闻编辑团队"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose prose-lg max-w-none mb-14">
            <h2 className="font-heading font-bold text-xl text-foreground-900 mb-4">品牌介绍</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">
              911黑料网创立于{siteInfo.foundedYear}年，是国内领先的独家爆料内容聚合平台。我们的使命是"让真相不再沉默"——在这个信息爆炸但真假难辨的时代，我们坚持用扎实的调查和严谨的求证，为读者还原事件的本来面目。
            </p>
            <p className="text-foreground-700 leading-relaxed mb-4">
              我们的编辑团队由{siteInfo.stats.verifiedReporters}名资深调查记者和行业分析师组成，每位成员都拥有5年以上的深度报道经验。我们建立了严格的多源交叉验证机制，确保每一条发布的信息都经过反复核实。
            </p>
            <p className="text-foreground-700 leading-relaxed mb-4">
              截至2026年，911黑料网已累计发布{siteInfo.stats.totalArticles}篇报道，其中{siteInfo.stats.exclusiveArticles}篇为独家首发。网站日均独立访客超过{formatNumber(siteInfo.stats.dailyVisitors)}人，已成为国内最具影响力的独立调查媒体之一。
            </p>
            <p className="text-foreground-700 leading-relaxed">
              我们的报道多次被主流媒体引用，并推动了多项社会问题的解决和政策改进。我们相信，真正的新闻力量来源于对真相的执着追求和对公众知情权的坚定守护。
            </p>
          </div>

          <div className="mb-14">
            <h2 className="font-heading font-bold text-xl text-foreground-900 mb-6">读者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="p-5 bg-background-50 rounded-lg border border-background-200/70"
                >
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-600 text-sm"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground-900 mb-4">联系我们</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">爆料邮箱</h3>
                    <p className="text-sm text-foreground-600">
                      如果您掌握一手内幕信息，请发送至
                      <a href={`mailto:${siteInfo.email}`} className="text-primary-600 hover:text-primary-700 font-medium">
                        {siteInfo.email}
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-shield-check-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">匿名举报通道</h3>
                    <p className="text-sm text-foreground-600">
                      我们提供端到端加密的匿名举报通道，确保爆料人身份信息不会被追踪。所有提交的信息仅由核心编辑团队可见。
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-scales-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">法务联系</h3>
                    <p className="text-sm text-foreground-600">
                      如需就内容真实性或侵权问题进行沟通，请联系 law@911heilian.com。我们会在48小时内响应。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading font-bold text-xl text-foreground-900 mb-4">数据亮点</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-1">{siteInfo.stats.totalArticles}</div>
                  <div className="text-xs text-foreground-500">累计爆料文章</div>
                </div>
                <div className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                  <div className="text-3xl font-heading font-bold text-accent-500 mb-1">{siteInfo.stats.exclusiveArticles}</div>
                  <div className="text-xs text-foreground-500">独家首发报道</div>
                </div>
                <div className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-1">{formatNumber(siteInfo.stats.dailyVisitors)}</div>
                  <div className="text-xs text-foreground-500">日均独立访客</div>
                </div>
                <div className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                  <div className="text-3xl font-heading font-bold text-accent-500 mb-1">{siteInfo.stats.verifiedReporters}</div>
                  <div className="text-xs text-foreground-500">认证调查记者</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-foreground-900 mb-6">常见问题</h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="font-medium text-foreground-900 text-sm md:text-base pr-6">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4 text-sm text-foreground-700 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

function formatNumber(num: number): string {
  if (num >= 10000) return `${Math.floor(num / 10000)}万`;
  return num.toString();
}